import React, { useState, useEffect } from 'react';
import 'dayjs/locale/de'; // Deutsche Sprache laden
import {
  Dialog,
  Tooltip,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  CardMedia,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useProducts } from '../ProductContext';
import { saveItem, strapiRemoteUri } from '../strapi/queries';
import { createFilterOptions } from '@mui/material/Autocomplete';

dayjs.locale('de');

/**
 * Modal component for adding a new item
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {string} props.containerId - ID of the container to add the item to
 * @returns {JSX.Element} - The modal component
 */
const AddItemModal = ({ open, onClose, containerId, onSuccess }) => {
  // Get products from context
  const { products, isLoading } = useProducts();

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) => option.name,
  });
  //const filterOptions2 = (options, { inputValue }) => matchSorter(options, inputValue);


  // State for form steps
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Produktauswahl', 'Variante wählen', 'Details eingeben'];

  // State for form values
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [selectedProductGroup, setSelectedProductGroup] = useState(null);
  const [selectedProductItem, setSelectedProductItem] = useState(null);
  const [selectedProductDocumentId, setSelectedProductDocumentId] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [itemDetailsList, setItemDetailsList] = useState([{
    purchaseDate: dayjs(),
    produceDate: dayjs(),
    lifespan: null,
    serialNumber: '',
    _internalId: Date.now() // Unique ID for key prop
  }]);

  // State for available options
  const [availableProductGroups, setAvailableProductGroups] = useState([]);
  const [availableProductsFiltered, setAvailableProductsFiltered] = useState([]);
  const [availableVariants, setAvailableVariants] = useState([]);

  // State for saving
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Reset form when modal is opened/closed
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  // Effect to update available product groups and products based on selected manufacturer
  useEffect(() => {
    if (products) {
      let filteredProducts = products;

      if (selectedManufacturer) {
        filteredProducts = products.filter(p => p.documentId === selectedManufacturer.documentId);
      }

      const groups = new Set();
      filteredProducts.forEach(producer => {
        producer.items.forEach(item => {
          if (item.category) {
            groups.add(item.category.name);
          }
        });
      });
      setAvailableProductGroups(Array.from(groups).sort());

      let items = [];
      filteredProducts.forEach(producer => {
        producer.items.forEach(item => {
          if (!selectedProductGroup || item.category.name === selectedProductGroup) {
            items.push(item);
          }
        });
      });
      const sortedWithId = items
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((item, index) => ({
          ...item,
          _internalId: index,
        }));

      setAvailableProductsFiltered(sortedWithId);

      // Reset product and variant selections if filters change
      setSelectedProductItem(null);
      setSelectedProductDocumentId('');
      setSelectedColor('');
    }
  }, [selectedManufacturer, selectedProductGroup, products]);

  // Update available variants when product item changes
  useEffect(() => {
    if (selectedProductItem) {
      setItemDetailsList(prevList => prevList.map(item => ({
        ...item,
        lifespan: selectedProductItem.lifespan
      })));
    }
    if (selectedProductItem && selectedProductItem.variant && selectedProductItem.variant.length > 0) {
      setAvailableVariants(selectedProductItem.variant);
      // If there's only one variant, select it automatically
      if (selectedProductItem.variant.length === 1) {
        setSelectedColor(selectedProductItem.variant[0].color);
      } else {
        setSelectedColor('');
      }
    } else {
      setAvailableVariants([]);
      setSelectedColor('');
    }
  }, [selectedProductItem]);

  const handleAddBlock = () => {
    setItemDetailsList(prevList => [...prevList, {
      purchaseDate: dayjs(),
      produceDate: dayjs(),
      lifespan: selectedProductItem.lifespan || null,
      serialNumber: '',
      _internalId: Date.now()
    }]);
  };

  const handleRemoveBlock = (idToRemove) => {
    setItemDetailsList(prevList => prevList.filter(item => item._internalId !== idToRemove));
  };

  const handleItemDetailChange = (id, field, value) => {
    setItemDetailsList(prevList =>
      prevList.map(item =>
        item._internalId === id ? { ...item, [field]: value } : item
      )
    );
  };

  // Reset form to initial state
  const resetForm = () => {
    setActiveStep(0);
    setSelectedManufacturer(null);
    setSelectedProductGroup(null);
    setSelectedProductItem(null);
    setSelectedProductDocumentId('');
    setSelectedColor('');
    setItemDetailsList([{
      purchaseDate: dayjs(),
      produceDate: dayjs(),
      lifespan: null,
      serialNumber: '',
      _internalId: Date.now()
    }]);
    setSaveError(null);
  };

  // Handle closing the modal
  const handleClose = () => {
    onClose();
  };

  // Handle next step
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  // Handle back step
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Handle manufacturer selection
  const handleManufacturerChange = (event, newValue) => {
    setSelectedManufacturer(newValue);
    setSelectedProductGroup(null); // Reset product group when manufacturer changes
  };

  // Handle product group selection
  const handleProductGroupChange = (event, newValue) => {
    setSelectedProductGroup(newValue);
  };

  // Handle product item selection
  const handleProductItemChange = (event, newValue) => {
    setSelectedProductItem(newValue);
    if (newValue) {
      setSelectedProductDocumentId(newValue.documentId);
    } else {
      setSelectedProductDocumentId('');
    }
  };

  // Handle variant selection
  const handleColorChange = (event) => {
    setSelectedColor(event.target.value);
  };

  // Handle save
  const handleSave = async () => {
    if (!containerId || !selectedProductDocumentId || !selectedColor) {
      setSaveError('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const savePromises = itemDetailsList.map(itemDetails => {
        const itemData = {
          container_id: containerId,
          item_id: selectedProductDocumentId,
          color: selectedColor,
          purchase_date: itemDetails.purchaseDate,
          produce_date: itemDetails.produceDate,
          serial: itemDetails.serialNumber,
          lifespan: itemDetails.lifespan
        };
        return saveItem(itemData);
      });

      const results = await Promise.all(savePromises);

      const hasError = results.some(result => result && result.error);

      if (hasError) {
        setSaveError('Fehler beim Speichern eines oder mehrerer Items.');
      } else {
        // Success - reload container and close modal
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      }
    } catch (error) {
      setSaveError('Fehler beim Speichern: ' + (error.message || 'Unbekannter Fehler'));
    } finally {
      setIsSaving(false);
    }
  };

  // Get the product image URL for the selected variant
  const getProductImageUrl = () => {
    if (!selectedColor || !availableVariants) return null;

    const variant = availableVariants.find(v => v.color === selectedColor);
    if (variant && variant.pic && variant.pic.url) {
      return strapiRemoteUri + variant.pic.url;
    }

    return null;
  };

  // Render step content based on active step
  const getStepContent = (step) => {
    switch (step) {
      case 0: // Product selection step
        return (
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              options={products || []}
              getOptionLabel={(option) => option.name || ''}
              value={selectedManufacturer}
              onChange={handleManufacturerChange}
              filterOptions={filterOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Hersteller"
                  variant="outlined"
                  placeholder="Auswählen zum filtern"
                />
              )}
              sx={{ mb: 2 }}
            />

            <Autocomplete
              options={availableProductGroups}
              getOptionLabel={(option) => option || ''}
              value={selectedProductGroup}
              onChange={handleProductGroupChange}
              filterOptions={filterOptions}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'flip',
                      enabled: false
                    },
                    {
                      name: 'preventOverflow',
                      enabled: false
                    }
                  ]
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Produktgruppe"
                  variant="outlined"
                  placeholder="Auswählen zum filtern"
                />
              )}
              sx={{ mb: 2 }}
            />

            <Autocomplete
              options={availableProductsFiltered}
              getOptionLabel={(option) => option.name || ''}
              value={selectedProductItem}
              onChange={handleProductItemChange}
              filterOptions={filterOptions}
              slotProps={{
                listbox: {
                  style: {
                    maxHeight: "150px",
                  },
                },
                popper: {
                  modifiers: [
                    {
                      name: 'flip',
                      enabled: false
                    },
                    {
                      name: 'preventOverflow',
                      enabled: false
                    }
                  ]
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  key={params.documentId + "tx"}
                  label="Produkt"
                  variant="outlined"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props} key={option._internalId} >
                  {option.variant && option.variant[0] && option.variant[0].pic && (
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                      }}
                    >
                      <img
                        style={{ borderRadius: "50%" }}
                        loading="lazy"
                        height="50"
                        src={strapiRemoteUri + option.variant[0].pic.url}
                        alt={option.name}
                        key={option._internalId + "img"}
                      />
                    </Box>
                  )}
                  {option.name}
                </Box>
              )}
            />
            {selectedManufacturer && selectedManufacturer.logo && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CardMedia
                  component="img"
                  sx={{
                    height: 80,
                    maxWidth: '100%',
                    objectFit: 'contain'
                  }}
                  image={strapiRemoteUri + selectedManufacturer.logo.url}
                  alt={selectedManufacturer.name}
                />
              </Box>
            )}
          </Box>
        );

      case 1: // Variant selection
        return (
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="color-select-label">Farbe</InputLabel>
              <Select
                labelId="color-select-label"
                id="color-select"
                value={selectedColor}
                label="Farbe"
                onChange={handleColorChange}
                disabled={availableVariants.length <= 1}
              >
                {availableVariants.map((variant) => (
                  <MenuItem key={variant.color} value={variant.color}>
                    {variant.color}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedColor && getProductImageUrl() && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CardMedia
                  component="img"
                  sx={{
                    height: 150,
                    maxWidth: '100%',
                    objectFit: 'contain'
                  }}
                  image={getProductImageUrl()}
                  alt={selectedProductItem?.name}
                />
              </Box>
            )}
          </Box>
        );

      case 2: // Details
        return (
          <Box sx={{ mt: 2 }}>
            {itemDetailsList.map((itemDetails, index) => (
              <Box key={itemDetails._internalId} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                <Grid container sx={{ justifyContent: "center" }} spacing={2}>
                  <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {itemDetailsList.length > 1 && (
                      <IconButton
                        onClick={() => handleRemoveBlock(itemDetails._internalId)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
                      <DatePicker
                        label="Kaufdatum"
                        value={itemDetails.purchaseDate}
                        onChange={(newValue) => handleItemDetailChange(itemDetails._internalId, 'purchaseDate', newValue)}
                        renderInput={(params) => <TextField {...params} fullWidth />}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: 'outlined'
                          }
                        }}
                        format="MM/YYYY"
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
                      <DatePicker
                        label="Herstelldatum"
                        value={itemDetails.produceDate}
                        views={['month', 'year']}
                        format="MM/YYYY"
                        onChange={(newValue) => handleItemDetailChange(itemDetails._internalId, 'produceDate', newValue)}
                        renderInput={(params) => <TextField {...params} fullWidth />}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: 'outlined'
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      fullWidth
                      label="Seriennummer"
                      value={itemDetails.serialNumber}
                      onChange={(e) => handleItemDetailChange(itemDetails._internalId, 'serialNumber', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      type="number"
                      fullWidth
                      label="Lebensdauer"
                      value={itemDetails.lifespan}
                      onChange={(e) => handleItemDetailChange(itemDetails._internalId, 'lifespan', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Tooltip title="Weiteres Item hinzufügen">
                <IconButton
                  aria-label="add block"
                  onClick={handleAddBlock}
                  sx={{
                    border: "2px solid #10527e",
                    margin: "2px",
                    backgroundColor: '#10527e',
                    color: 'white',
                    '&:hover': {
                      border: "2px solid #282c34",
                      backgroundColor: '#282c34',
                    }
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        );

      default:
        return 'Unbekannter Schritt';
    }
  };

  // Determine if the next button should be disabled
  const isNextDisabled = () => {
    switch (activeStep) {
      case 0:
        return !selectedProductItem;
      case 1:
        return !selectedColor;
      default:
        return false;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        overflow: "visible",
        '& .MuiDialog-paper': {
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle>Neues Item hinzufügen</DialogTitle>
      <DialogContent>
        <Box sx={{ width: '100%', mt: 1 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {getStepContent(activeStep)}

              {saveError && (
                <Typography color="error" sx={{ mt: 2 }}>
                  {saveError}
                </Typography>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button sx={{ color: "#10527e" }} onClick={handleClose}>
          Abbrechen
        </Button>

        {activeStep > 0 && (
          <Button sx={{ color: "#10527e" }} onClick={handleBack}>
            Zurück
          </Button>
        )}

        {activeStep < steps.length - 1 ? (
          <Button
            onClick={handleNext}
            disabled={isNextDisabled()}
            sx={{
              backgroundColor: '#10527e',
              color: 'white',
              '&:hover': {
                backgroundColor: '#282c34',
              },
              '&.Mui-disabled': {
                backgroundColor: '#cccccc',
                color: '#666666'
              }
            }}
          >
            Weiter
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            disabled={isSaving}
            variant="contained"
            sx={{
              backgroundColor: '#10527e',
              color: 'white',
              '&:hover': {
                backgroundColor: '#282c34',
              },
              '&.Mui-disabled': {
                backgroundColor: '#cccccc',
                color: '#666666'
              }
            }}
          >
            {isSaving ? <CircularProgress size={24} /> : 'Speichern'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddItemModal;